/**
 * Configuración → Servicios → Comprobantes (`/configuracion/servicios/documentos`).
 *
 * Detalle de los comprobantes electrónicos por origen y estado: **pendientes**
 * (sin respuesta de SUNAT) y **rechazados** (que también hay que atender). Se
 * llega desde los widgets de la pantalla de *Servicios de la estación*.
 */

import { ArrowLeft, FileWarning, RefreshCw, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, SectionCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatDateTimeValue, formatNumber } from '@/lib/utils';

import { useDocumentosServicios } from '../servicios.api';
import type { EstadoComprobanteFiltro, OrigenComprobante } from '../servicios.types';

const ORIGENES: { valor: OrigenComprobante; etiqueta: string }[] = [
  { valor: 'todos', etiqueta: 'Todos los orígenes' },
  { valor: 'factura', etiqueta: 'Facturas (POS)' },
  { valor: 'boleta', etiqueta: 'Boletas (POS)' },
  { valor: 'admin_comprobante', etiqueta: 'Administrativos' },
  { valor: 'market', etiqueta: 'Market' },
];

const ESTADOS: { valor: EstadoComprobanteFiltro; etiqueta: string }[] = [
  { valor: 'pendientes', etiqueta: 'Pendientes' },
  { valor: 'rechazados', etiqueta: 'Rechazados' },
  { valor: 'todos', etiqueta: 'Todos' },
];

function esOrigen(valor: string | null): valor is OrigenComprobante {
  return ORIGENES.some((origen) => origen.valor === valor);
}

function esEstado(valor: string | null): valor is EstadoComprobanteFiltro {
  return ESTADOS.some((estado) => estado.valor === valor);
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;

  return porDefecto;
}

/** Fecha del comprobante: las de factura/boleta vienen sin hora. */
function formatearFecha(fecha: string | null): string {
  if (!fecha) return '—';

  return fecha.length > 10 ? formatDateTimeValue(fecha) : formatDateValue(fecha);
}

/** Color de la insignia según el estado del comprobante. */
function tonoEstado(estado: string): 'danger' | 'warning' | 'success' | 'neutral' {
  const valor = estado.toLowerCase();

  if (valor.includes('rechaz')) return 'danger';
  if (valor.includes('valid') || valor.includes('pendient') || valor === 'sin estado') return 'warning';
  if (valor.includes('acept')) return 'success';

  return 'neutral';
}

export function ComprobantesPage() {
  useDocumentTitle('Configuración · Comprobantes electrónicos');

  const [parametros, setParametros] = useSearchParams();
  const origenParam = parametros.get('origen');
  const estadoParam = parametros.get('estado');

  const origen: OrigenComprobante = esOrigen(origenParam) ? origenParam : 'todos';
  const estado: EstadoComprobanteFiltro = esEstado(estadoParam) ? estadoParam : 'pendientes';

  const [busqueda, setBusqueda] = useState('');
  const consulta = useDocumentosServicios(origen, estado);

  const cambiar = (clave: 'origen' | 'estado', valor: string) => {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set(clave, valor);
    setParametros(siguientes, { replace: true });
  };

  const termino = busqueda.trim().toLowerCase();
  const documentos = (consulta.data?.documentos ?? []).filter(
    (documento) =>
      termino === '' ||
      documento.documento.toLowerCase().includes(termino) ||
      documento.cliente.toLowerCase().includes(termino) ||
      documento.estado.toLowerCase().includes(termino),
  );

  const resumen = consulta.data?.resumen ?? [];
  const pendientes = resumen.reduce((suma, fila) => suma + fila.pendientes, 0);
  const rechazados = resumen.reduce((suma, fila) => suma + fila.rechazados, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/configuracion/servicios"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
          >
            <ArrowLeft className="size-3" aria-hidden="true" />
            Servicios de la estación
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <FileWarning className="size-6 text-brand-500" aria-hidden="true" />
            Comprobantes electrónicos
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-500">
            Pendientes (sin respuesta de SUNAT) y rechazados, por origen. Los rechazados también hay
            que atenderlos: hay que corregirlos y reenviarlos.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          loading={consulta.isFetching}
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink-700">Origen</span>
              <Select value={origen} onChange={(evento) => cambiar('origen', evento.target.value)}>
                {ORIGENES.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink-700">Estado</span>
              <Select value={estado} onChange={(evento) => cambiar('estado', evento.target.value)}>
                {ESTADOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </div>

            <Input
              label="Buscar"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Documento, cliente o estado…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral">{formatNumber(consulta.data?.total ?? 0)} fila(s)</Badge>
            <Badge variant={pendientes > 0 ? 'warning' : 'success'}>
              Pendientes: {formatNumber(pendientes)}
            </Badge>
            <Badge variant={rechazados > 0 ? 'danger' : 'success'}>
              Rechazados: {formatNumber(rechazados)}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <SectionCard
        title="Comprobantes"
        description="Hasta 300 filas por consulta; acota con los filtros o la búsqueda."
      >
        {consulta.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : consulta.isError ? (
          <Alert variant="error" title="No se pudieron leer los comprobantes">
            {mensajeDe(consulta.error, 'La API no respondió. Vuelve a intentarlo.')}
          </Alert>
        ) : documentos.length === 0 ? (
          <Alert variant="info" title="Sin resultados">
            No hay comprobantes con estos filtros.
          </Alert>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  {origen === 'todos' ? (
                    <th className="px-3 py-2 text-left font-semibold">Origen</th>
                  ) : null}
                  <th className="px-3 py-2 text-left font-semibold">Documento</th>
                  <th className="px-3 py-2 text-left font-semibold">Cliente</th>
                  <th className="px-3 py-2 text-left font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold">Estado</th>
                  <th className="px-3 py-2 text-right font-semibold">Monto</th>
                </tr>
              </thead>
              <tbody>
                {documentos.map((documento) => (
                  <tr
                    key={`${documento.origen}-${documento.id}`}
                    className="border-t border-ink-100"
                  >
                    {origen === 'todos' ? (
                      <td className="whitespace-nowrap px-3 py-2 text-ink-600">
                        {documento.etiqueta}
                      </td>
                    ) : null}
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-ink-800">
                      {documento.documento || '—'}
                    </td>
                    <td
                      className="max-w-[18rem] truncate px-3 py-2 text-ink-700"
                      title={documento.cliente}
                    >
                      {documento.cliente || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink-600">
                      {formatearFecha(documento.fecha)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={tonoEstado(documento.estado)}>{documento.estado || '—'}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-800">
                      {documento.monto === null ? '—' : formatCurrency(documento.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
